import cx from "classnames";
import { isEqual } from "lodash";
import { JSX, useContext, useMemo, useState } from "react";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { BenchmarkingState } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingState";
import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton, QESButtonTypeClasses } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FlyoutLink } from "@app/shared/Flyouts";
import QHPAdvancedFilters from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPAdvancedFilters";
import { QHPFiltersContext } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPFiltersContext";
import { QHPNetworkTypeEnum } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetworkTypeEnum";
import { QHPPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPPrimaryFiltersPayload";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";

const QHPPrimaryFilters = (): JSX.Element => {
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const qhpFiltersContext = useContext(QHPFiltersContext);

  const [isAdvancedFiltersDirty, setIsAdvancedFiltersDirty] = useState<boolean>(false);

  const hasFilterData = !!benchmarkingFiltersContext.states.length;

  const applyDisabled = (): boolean => {
    // do not allow button interactions if ANYTHING is loading
    if (benchmarkingFiltersContext.linesOfBusinessLoading || qhpFiltersContext.primaryFiltersLoading || qhpFiltersContext.networkFiltersLoading || !hasFilterData) {
      return true;
    }

    const countiesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
      benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
    );

    const starRatingsEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.starRatings?.sort(),
      qhpFiltersContext.selectedStarRatings.map(x => x.key).sort()
    );

    const networkTypesEqual = isEqual(
      (benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload)?.networkType,
      qhpFiltersContext.selectedNetworkType
    );

    const planTypesEqual = isEqual(
      benchmarkingFiltersContext.displayedPrimaryFilters?.planTypes?.sort(),
      qhpFiltersContext.selectedPlanTypes.map(x => x.key).sort()
    );

    const marketCoverageEqual = isEqual(
      (benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload)?.marketCoverage?.sort(),
      qhpFiltersContext.selectedMarketCoverage.map(x => x.key).sort()
    );

    // in order to avoid unnecessary api calls, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one county, a line of business, a star rating, a plan type, a market coverage, and a network type are not selected
    const anyChanged = !countiesEqual || !starRatingsEqual || !networkTypesEqual || !planTypesEqual || !marketCoverageEqual;
    const allSelected = benchmarkingFiltersContext.selectedCounties.length > 0 &&
      !!benchmarkingFiltersContext.selectedLineOfBusiness &&
      qhpFiltersContext.selectedStarRatings.length > 0 &&
      qhpFiltersContext.selectedPlanTypes.length > 0 &&
      qhpFiltersContext.selectedMarketCoverage.length > 0 &&
      !!qhpFiltersContext.selectedNetworkType;

    return !(anyChanged && allSelected);
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    const loading = benchmarkingFiltersContext.linesOfBusinessLoading || qhpFiltersContext.primaryFiltersLoading;
    const isDefault = benchmarkingFiltersContext.selectedCounties.length === 0 &&
      !benchmarkingFiltersContext.selectedState &&
      qhpFiltersContext.selectedStarRatings.length === qhpFiltersContext.mappedStarRatings.length &&
      qhpFiltersContext.selectedPlanTypes.length === qhpFiltersContext.planTypes.length &&
      qhpFiltersContext.selectedMarketCoverage.length === qhpFiltersContext.marketCoverages.length &&
      qhpFiltersContext.selectedNetworkType === QHPNetworkTypeEnum.All;

    return loading || isDefault;
  }

  const onCBSAMouseOver = (parentKey?: string): void => {
    if (!!parentKey) {
      const cbsa: NestingParentDropdownItem = benchmarkingFiltersContext.counties.find(x => x.key === parentKey);
      benchmarkingFiltersContext.setMousedOverCountyIds(cbsa?.children.map(x => x.key));
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onCountyCheckboxMouseOver = (dropdownItemKey?: string): void => {
    if (!!dropdownItemKey) {
      benchmarkingFiltersContext.setMousedOverCountyIds([dropdownItemKey]);
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  const onStateChange = (stateAbbreviation: string): void => {
    const state: BenchmarkingState = benchmarkingFiltersContext.states.find(x => x.stateAbbreviation === stateAbbreviation);

    if (!!state) {
      benchmarkingFiltersContext.setSelectedState(state);
    }
  }

  // drives the notification pip on the Advanced Filters button — true whenever any of the
  // advanced-only fields (CMS Star Rating, Plan Type, Market Coverage, Network Type) are non-default
  const showAdvancedFiltersIndicator = useMemo<boolean>(() => {
    const starRatingsDefault = qhpFiltersContext.selectedStarRatings.length === qhpFiltersContext.mappedStarRatings.length;
    const planTypesDefault = qhpFiltersContext.selectedPlanTypes.length === qhpFiltersContext.planTypes.length;
    const marketCoverageDefault = qhpFiltersContext.selectedMarketCoverage.length === qhpFiltersContext.marketCoverages.length;
    const networkTypeDefault = qhpFiltersContext.selectedNetworkType === QHPNetworkTypeEnum.All;

    return !starRatingsDefault || !planTypesDefault || !marketCoverageDefault || !networkTypeDefault;
  }, [
    qhpFiltersContext.selectedStarRatings,
    qhpFiltersContext.mappedStarRatings,
    qhpFiltersContext.selectedPlanTypes,
    qhpFiltersContext.planTypes,
    qhpFiltersContext.selectedMarketCoverage,
    qhpFiltersContext.marketCoverages,
    qhpFiltersContext.selectedNetworkType,
  ]);

  return (
    <div className="ci-sticky-header">
      <div className="row">
        <div className="col-12">
          <label className="label-m mb-2">
            Filters
          </label>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <QESSelect
            disabled={benchmarkingFiltersContext.linesOfBusinessLoading || !benchmarkingFiltersContext.linesOfBusiness.length}
            testId="network-benchmarking-dashboard__primary-filters--line-of-business"
            label="Line of Business"
            onChange={(e) => benchmarkingFiltersContext.setSelectedLineOfBusiness(parseInt(e.target.value))}
            value={benchmarkingFiltersContext.selectedLineOfBusiness || ""}
          >
            {Object.values(benchmarkingFiltersContext.linesOfBusiness).map((lineOfBusiness, index) => (
              <option
                key={index}
                value={lineOfBusiness}
              >
                {BenchmarkingLineOfBusinessEnum[lineOfBusiness]}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <QESSelect
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
            testId="network-benchmarking-dashboard__primary-filters--state"
            label="State"
            onChange={(e) => onStateChange(e.target.value)}
            value={benchmarkingFiltersContext.selectedState?.stateAbbreviation || ""}
          >
            {benchmarkingFiltersContext.states.map((state, index) => (
              <option
                key={index}
                value={state.stateAbbreviation}
              >
                {state.stateName}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col">
          <MultiselectNestedDropdown
            containerClassName="d-block"
            customSelectInputText={(!!benchmarkingFiltersContext.rawCounties.length && benchmarkingFiltersContext.rawCounties.length === benchmarkingFiltersContext.selectedCounties.length) ? "All" : null}
            disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData || !benchmarkingFiltersContext.selectedState}
            hasDuplicates={true}
            items={benchmarkingFiltersContext.counties}
            label="Service Area"
            onDropdownItemMouseOver={onCountyCheckboxMouseOver}
            onParentMouseOver={onCBSAMouseOver}
            onSelectionChanged={(selectedCounties: string[]) => { benchmarkingFiltersContext.setSelectedCounties(selectedCounties) }}
            selectedItems={benchmarkingFiltersContext.selectedCounties}
            testId="network-benchmarking-dashboard__primary-filters--county"
          />
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <FlyoutLink
              classNames={cx(QESButtonTypeClasses.get("outline"), "position-relative")}
              content={{
                body: <QHPAdvancedFilters onDirtyChange={setIsAdvancedFiltersDirty} />,
                title: "Advanced Filters",
              }}
              disabled={qhpFiltersContext.primaryFiltersLoading || !hasFilterData}
              modalContent={{
                body: "You will lose unsaved changes. Continue with closing the sidebar?",
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                title: "Warning",
              }}
              size="40"
              testId="competitive-insights__primary-filters--advanced-filters"
              warnOnClose={isAdvancedFiltersDirty}
            >
              {showAdvancedFiltersIndicator && <div id="competitive-insights__primary-filters--advanced-filters-pip" className="qes-button__notification-pip" />}
              Advanced Filters
            </FlyoutLink>
          </div>
        </div>
        <div className="col d-flex">
          <div className="align-self-end">
            <QESButton
              disabled={applyDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--apply"
              qesType="secondary"
              onClick={() => qhpFiltersContext.onApplyPrimaryFiltersClick()}
            >
              Apply
            </QESButton>

            <QESButton
              disabled={clearDisabled()}
              qesTestId="network-benchmarking-dashboard__primary-filters--clear"
              qesType="secondary-danger"
              onClick={() => qhpFiltersContext.onClearPrimaryFiltersClick()}
              className="ms-2"
            >
              Clear
            </QESButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QHPPrimaryFilters;
